import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CalendarPlus, CheckCircle2, ShieldCheck, Star, XCircle } from "lucide-react";
import { DashboardLayout, Panel } from "@/components/DashboardLayout";
import { EscrowChip, StarPicker } from "@/components/Ratings";
import { FileField } from "@/components/KycFields";
import { LocationPin } from "@/components/LocationPin";
import { SearchableSelect } from "@/components/SearchableSelect";
import { api, apiError, naira } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NAV = [
  { to: "/dashboard/master-client", label: "Overview", testId: "nav-overview" },
  { to: "/beneficiaries", label: "Beneficiaries", testId: "nav-beneficiaries" },
  { to: "/witnesses", label: "Witnesses", testId: "nav-witnesses" },
  { to: "/marketplace", label: "Find providers", testId: "nav-marketplace" },
  { to: "/bookings", label: "Bookings", testId: "nav-bookings" },
];

export const BOOKING_TONE = {
  REQUESTED: "bg-amber-50 text-amber-700 border-amber-200",
  ACCEPTED: "bg-sky-50 text-sky-700 border-sky-200",
  AWAITING_CONFIRMATION: "bg-orange-50 text-orange-700 border-orange-200",
  CONFIRMED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  DECLINED: "bg-red-50 text-red-700 border-red-200",
  CANCELLED: "bg-slate-100 text-slate-600 border-slate-200",
};

export const BookingCard = ({ b, children, testId }) => (
  <li className="flex flex-wrap items-start justify-between gap-4 py-5" data-testid={testId}>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display font-bold text-slate-900">{b.service_name}</p>
        <span
          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${BOOKING_TONE[b.status] || BOOKING_TONE.CANCELLED}`}
          data-testid={`booking-status-${b.id}`}
        >
          {b.status.replaceAll("_", " ")}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {b.provider_name} · {b.provider_category} · {naira(b.fee_ngn)}
      </p>
      <p className="mt-1 text-sm text-slate-500">
        For {b.beneficiary_name} at {b.service_address}
      </p>
      <p className="mt-1 text-xs text-slate-400">
        {new Date(b.scheduled_at).toLocaleString()} · Witness: {b.witness_name || "none selected"}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <EscrowChip status={b.payment_status} dueAt={b.release_due_at} testId={`escrow-chip-${b.id}`} />
        {b.settlement && (
          <span className="text-xs text-slate-500" data-testid={`booking-split-${b.id}`}>
            Practitioner {naira(b.settlement.provider_share_ngn)} · CARENEST fee {naira(b.settlement.platform_fee_ngn)}
          </span>
        )}
      </div>
      {b.notes && <p className="mt-2 text-xs text-slate-500">Note: {b.notes}</p>}
      {b.service_geolocation && (
        <LocationPin geolocation={b.service_geolocation} address={b.service_address} testId={`booking-map-${b.id}`} />
      )}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </li>
);

export function ClientBookings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [witnesses, setWitnesses] = useState([]);
  const [f, setF] = useState({ provider_id: "", service_id: "", beneficiary_id: "", witness_id: "", scheduled_at: "", notes: "" });
  const [escrow, setEscrow] = useState(null);
  const [rating, setRating] = useState(null);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [dispute, setDispute] = useState(null);
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState([]);

  const submitDispute = async () => {
    try {
      await api.post(`/api/escrow/bookings/${dispute.id}/dispute`, { reason, evidence_file_ids: evidence });
      toast.success("Problem flagged", { description: "Funds stay in escrow until an administrator reviews it." });
      setDispute(null);
      setReason("");
      setEvidence([]);
      load();
      loadEscrow();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadEscrow = () => api.get("/api/escrow/summary").then((r) => setEscrow(r.data)).catch(() => {});

  const fund = async (id) => {
    try {
      const { data } = await api.post(`/api/escrow/bookings/${id}/fund`, {
        callback_url: `${window.location.origin}/payment/callback`,
      });
      toast.message("Opening secure checkout", { description: `${naira(data.amount_ngn)} via Paystack` });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitRating = async () => {
    try {
      await api.post(`/api/bookings/${rating.id}/rating`, { stars, comment: comment || null });
      toast.success("Thank you for rating this provider");
      setRating(null);
      setStars(5);
      setComment("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = () => api.get("/api/bookings").then((r) => setItems(r.data.items)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    api.get("/api/marketplace/providers").then((r) => setProviders(r.data.providers)).catch(() => {});
    api.get("/api/beneficiaries").then((r) => setBeneficiaries(r.data)).catch(() => {});
    api.get("/api/witnesses").then((r) => setWitnesses(r.data)).catch(() => {});
    loadEscrow();
  }, []);

  const selectedProvider = providers.find((p) => p.id === f.provider_id);

  const createBeneficiary = async (fullName) => {
    try {
      const { data } = await api.post("/api/beneficiaries/quick", { full_name: fullName, relationship: "Family member" });
      setBeneficiaries((prev) => [...prev, data]);
      toast.success(`${data.full_name} added`, { description: "Complete their full record on the Beneficiaries page." });
      return { value: data.id };
    } catch (e) {
      toast.error(apiError(e));
      return null;
    }
  };

  const createWitness = async (fullName) => {
    try {
      const { data } = await api.post("/api/witnesses/quick", { full_name: fullName, phone: "0000000000" });
      setWitnesses((prev) => [...prev, data]);
      toast.success(`${data.full_name} added`, { description: "Add their phone, ID and signed form on the Witnesses page." });
      return { value: data.id };
    } catch (e) {
      toast.error(apiError(e));
      return null;
    }
  };

  const create = async () => {
    try {
      await api.post("/api/bookings", {
        ...f,
        witness_id: f.witness_id || null,
        notes: f.notes || null,
        scheduled_at: new Date(f.scheduled_at).toISOString(),
      });
      toast.success("Care request sent to the provider");
      setOpen(false);
      setF({ provider_id: "", service_id: "", beneficiary_id: "", witness_id: "", scheduled_at: "", notes: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/api/bookings/${id}/${action}`, {});
      toast.success(action === "confirm" ? "Visit confirmed" : "Booking cancelled");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const valid = f.provider_id && f.service_id && f.beneficiary_id && f.scheduled_at;

  return (
    <DashboardLayout title="Care bookings" subtitle="Request a visit from a verified provider for one of your beneficiaries." nav={NAV}>
      {escrow && (
        <>
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900" data-testid="escrow-simulated-banner">
            Card payments run through <strong>Paystack{escrow.test_mode ? " test mode" : ""}</strong>
            {escrow.test_mode ? " — use a Paystack test card, no real money moves" : ""}. Your money is held in escrow,
            and on release the practitioner receives <strong>{100 - escrow.platform_fee_percent}%</strong> with a{" "}
            <strong>{escrow.platform_fee_percent}%</strong> CARENEST facilitation fee. You have{" "}
            <strong>{escrow.dispute_window_hours} hours</strong> after confirming a visit to flag a problem before funds leave escrow.
          </div>
          <div className="mb-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="escrow-summary">
            {[
              ["Held in escrow", naira(escrow.in_escrow_ngn), "escrow-held"],
              ["Awaiting payment", naira(escrow.pending_payment_ngn), "escrow-pending-payment"],
              ["Awaiting release", naira(escrow.pending_release_ngn), "escrow-pending-release"],
              ["Disputed", naira(escrow.disputed_ngn), "escrow-disputed"],
              ["Released", naira(escrow.released_ngn), "escrow-released"],
              ["Refunded", naira(escrow.refunded_ngn), "escrow-refunded"],
            ].map(([label, value, testId]) => (
              <div key={label} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700">{label}</p>
                <p className="mt-1.5 font-display text-lg font-extrabold text-slate-900" data-testid={testId}>{value}</p>
              </div>
            ))}
          </div>
        </>
      )}
      <Panel
        title={`${items.length} booking${items.length === 1 ? "" : "s"}`}
        testId="client-bookings-panel"
        action={
          <Button className="rounded-full bg-emerald-900 hover:bg-emerald-800" onClick={() => setOpen(true)} data-testid="new-booking-btn">
            <CalendarPlus className="mr-2 h-4 w-4" /> Request care
          </Button>
        }
      >
        {items.length ? (
          <ul className="divide-y divide-slate-100" data-testid="client-bookings-list">
            {items.map((b) => (
              <BookingCard key={b.id} b={b} testId={`booking-row-${b.id}`}>
                {b.can_fund && (
                  <Button size="sm" className="rounded-full bg-emerald-900" onClick={() => fund(b.id)} data-testid={`fund-booking-${b.id}`}>
                    <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Fund escrow
                  </Button>
                )}
                {["REQUESTED", "ACCEPTED"].includes(b.status) && (
                  <Button size="sm" variant="outline" onClick={() => act(b.id, "cancel")} data-testid={`cancel-booking-${b.id}`}>
                    Cancel
                  </Button>
                )}
                {b.status === "AWAITING_CONFIRMATION" && (
                  <Button size="sm" className="rounded-full bg-emerald-900" onClick={() => act(b.id, "confirm")} data-testid={`confirm-booking-${b.id}`}>
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Confirm visit
                  </Button>
                )}
                {b.payment_status === "PENDING_RELEASE" && (
                  <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => setDispute(b)} data-testid={`dispute-booking-${b.id}`}>
                    <AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> Flag a problem
                  </Button>
                )}
                {b.status === "CONFIRMED" && (
                  <Button size="sm" variant="outline" onClick={() => setRating(b)} data-testid={`rate-booking-${b.id}`}>
                    <Star className="mr-1.5 h-3.5 w-3.5" /> Rate provider
                  </Button>
                )}
              </BookingCard>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No bookings yet. Request care from a verified provider to get started.</p>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Request care</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Provider</Label>
              <select className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.provider_id} onChange={(e) => setF({ ...f, provider_id: e.target.value, service_id: "" })} data-testid="booking-provider-select">
                <option value="">Select a verified provider…</option>
                {providers.map((p) => <option key={p.id} value={p.id}>{`${p.full_name} — ${p.professional_category}`}</option>)}
              </select>
            </div>
            <div>
              <Label>Service</Label>
              <select className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.service_id} onChange={(e) => setF({ ...f, service_id: e.target.value })} disabled={!selectedProvider} data-testid="booking-service-select">
                <option value="">Select a service…</option>
                {(selectedProvider?.services || []).map((s) => <option key={s.id} value={s.id}>{`${s.name} — ${naira(s.fee_ngn)}`}</option>)}
              </select>
            </div>
            <div>
              <Label>Beneficiary</Label>
              <SearchableSelect
                value={f.beneficiary_id}
                options={beneficiaries.map((b) => ({
                  value: b.id,
                  label: b.full_name,
                  sublabel: b.needs_completion ? `${b.relationship} · details incomplete` : b.relationship,
                }))}
                onChange={(v) => setF({ ...f, beneficiary_id: v })}
                onCreate={createBeneficiary}
                placeholder="Search or type a new name…"
                createLabel={(text) => `Add “${text}” as a new beneficiary`}
                emptyLabel="No beneficiaries yet — type a name to add one"
                testId="booking-beneficiary-select"
              />
            </div>
            <div>
              <Label>Witness (optional)</Label>
              <SearchableSelect
                value={f.witness_id}
                options={[
                  { value: "", label: "No witness", sublabel: "Confirm the visit yourself" },
                  ...witnesses.map((w) => ({
                    value: w.id,
                    label: w.full_name,
                    sublabel: w.needs_completion ? `${w.phone} · ID & form still needed` : w.phone,
                  })),
                ]}
                onChange={(v) => setF({ ...f, witness_id: v })}
                onCreate={createWitness}
                placeholder="No witness — search or type a new name…"
                createLabel={(text) => `Add “${text}” as a new witness`}
                emptyLabel="No witnesses yet — type a name to add one"
                testId="booking-witness-select"
              />
            </div>
            <div>
              <Label>Date & time</Label>
              <Input type="datetime-local" className="mt-1.5" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} data-testid="booking-datetime-input" />
            </div>
            <div>
              <Label>Notes for the provider</Label>
              <Textarea className="mt-1.5" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="booking-notes-input" />
            </div>
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              Once the practitioner accepts, you can fund the visit securely with Paystack. Money stays in escrow until
              you confirm the visit took place.
            </p>
            <Button className="w-full rounded-full bg-emerald-900" disabled={!valid} onClick={create} data-testid="booking-save-btn">
              Send request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!rating} onOpenChange={(o) => !o && setRating(null)}>
        <DialogContent data-testid="rating-dialog">
          <DialogHeader><DialogTitle>Rate {rating?.provider_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              How was {rating?.service_name} for {rating?.beneficiary_name}? Your rating helps other families choose well.
            </p>
            <StarPicker value={stars} onChange={setStars} testId="rating-star-picker" />
            <Textarea
              placeholder="Share what went well or what could improve (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="rating-comment-input"
            />
            <Button className="w-full rounded-full bg-emerald-900" onClick={submitRating} data-testid="rating-submit-btn">
              Submit rating
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!dispute} onOpenChange={(o) => !o && setDispute(null)}>
        <DialogContent data-testid="dispute-dialog">
          <DialogHeader><DialogTitle>Flag a problem</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Tell us what went wrong with {dispute?.service_name}. The {naira(dispute?.fee_ngn)} stays in escrow until a
              CARENEST administrator reviews your report.
            </p>
            <Textarea
              placeholder="Describe the problem in at least a sentence"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="dispute-reason-input"
            />
            <FileField
              label={`Evidence (optional, ${evidence.length}/3 attached)`}
              hint="Attach a photo or document, e.g. a picture of the visit or a message thread"
              value={evidence.length ? evidence[evidence.length - 1] : ""}
              onChange={(id) => setEvidence((prev) => (prev.length >= 3 ? prev : [...prev, id]))}
              purpose="DISPUTE_EVIDENCE"
              testId="dispute-evidence-upload"
            />
            <Button
              className="w-full rounded-full bg-red-700 hover:bg-red-800"
              disabled={reason.trim().length < 10}
              onClick={submitDispute}
              data-testid="dispute-submit-btn"
            >
              Submit dispute
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export function ProviderBookings({ embedded = false }) {
  const [items, setItems] = useState([]);

  const load = () => api.get("/api/bookings").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/api/bookings/${id}/${action}`, {});
      toast.success(`Booking ${action === "complete" ? "marked as delivered" : `${action}ed`}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const body = (
    <Panel title="Care requests" description="Accept a request, then mark it delivered for witness confirmation." testId="provider-bookings-panel">
      {items.length ? (
        <ul className="divide-y divide-slate-100" data-testid="provider-bookings-list">
          {items.map((b) => (
            <BookingCard key={b.id} b={b} testId={`provider-booking-row-${b.id}`}>
              {b.status === "REQUESTED" && (
                <>
                  <Button size="sm" className="rounded-full bg-emerald-900" onClick={() => act(b.id, "accept")} data-testid={`accept-booking-${b.id}`}>
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => act(b.id, "decline")} data-testid={`decline-booking-${b.id}`}>
                    <XCircle className="mr-1.5 h-3.5 w-3.5" /> Decline
                  </Button>
                </>
              )}
              {b.status === "ACCEPTED" && (
                <Button size="sm" className="rounded-full bg-emerald-900" onClick={() => act(b.id, "complete")} data-testid={`complete-booking-${b.id}`}>
                  Mark delivered
                </Button>
              )}
            </BookingCard>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">No care requests yet.</p>
      )}
    </Panel>
  );

  if (embedded) return body;
  return <DashboardLayout title="Care requests" subtitle="Requests from verified clients.">{body}</DashboardLayout>;
}


